import React, { useState, useEffect, useRef, useCallback } from 'react';
import Hls from 'hls.js';
import {
  Maximize2, Minimize2, Eye, MapPin, Camera, MoreVertical, Pause, Play,
  Volume2, VolumeX, Tv, X, Pencil, Trash2,
} from 'lucide-react';
import {
  callHFAnalyze, enrichScanResult, renderAllBboxes, renderRawBboxes, dispatchAlerts,
} from '../lib/aiScanner';
import { createFaceVoteBuffer } from '../lib/faceVoteBuffer';
import { createTracker, extrapolate, scaleRawBoxes } from '../lib/realtimeScan';
import {
  detectStreamType, playWhep, hasGateway,
  gatewayWhepUrlForRtsp, isMixedContentBlocked,
} from '../lib/streamPlayer';
import { logScanResult } from '../lib/activityLog';
import { useApp } from '../contexts/AppContext';

const IMAGE_EXT_RE = /\.(jpg|jpeg|png|webp|gif|bmp)(\?|$)/i;

// Mode "semirip realtime" di atas HF Space: lebar maks frame terkirim, jeda minimum antar-permintaan per kamera,
// dan umur maks hasil sebelum kotak dihapus bila balasan berhenti datang.
const SEND_MAX_W = 1280;
const MIN_GAP_MS = 800;
const RESULT_MAX_AGE_MS = 4000;
const OVERLAY_FPS = 15;

function isImageItem(item) {
  if (!item) return false;
  if (item.kind === 'image') return true;
  if (item.kind === 'video') return false;
  return IMAGE_EXT_RE.test(item.url || item.nama || '');
}

// Titik tengah bbox `inner` berada di dalam bbox `outer`.
function inside(inner, outer) {
  const cx = (inner[0] + inner[2]) / 2, cy = (inner[1] + inner[3]) / 2;
  return cx >= outer[0] && cx <= outer[2] && cy >= outer[1] && cy <= outer[3];
}

function captureFrame(sourceEl, w, h) {
  return new Promise((resolve) => {
    try {
      const cap = document.createElement('canvas');
      cap.width = w; cap.height = h;
      cap.getContext('2d').drawImage(sourceEl, 0, 0, w, h);
      cap.toBlob((b) => resolve(b), 'image/jpeg', 0.85);
    } catch (e) {
      console.warn('[AutoScanPlayer] capture failed:', e?.message || e);
      resolve(null);
    }
  });
}

// ── Component ─────────────────────────────────────────────────────────────────
// Alias note: this component IS the CameraFeedCell — each cell is independent
// with its own canvasRef, scanRef, isScanning state, and per-cell scan loop.
// Stagger the start across cells via scanDelayMs (e.g. cellIndex*500ms).

export default function CameraFeedCell({
  kamera,
  cameraNumber,         // 01, 02, etc — passed from grid index
  embeddingsRef = { current: [] },
  scanInterval = 3000,
  scanDelayMs = 0,
  isMaximized = false,
  onToggleMaximize,
  isFullscreen = false,
  onToggleFullscreen,
  onEdit,               // callback (kamera) — buka edit form
  onDelete,             // callback (kamera) — hapus camera
  onSnapshot,           // callback (blob, kamera) — save snapshot
  scanEnabled = true,
  scanTurn = true,      // giliran scan (induk memutar bila banyak sel menyala)
  cellScanOn: cellScanProp, // saklar scan sel dari induk; kosong = state lokal
  onCellScanToggle,
  onScanResult, // optional — called with the full enriched scan result after each successful scan
  isTheatreMini = false, // compact rendering untuk Theatre Mode strip thumbnail
  mediaStream = null, // Jalur B: MediaStream langsung (mis. getDisplayMedia tangkap tab YouTube)
}) {
  const { t } = useApp();
  const playlist = kamera?.playlist || [];
  const isLive   = !!kamera?.stream_url || !!mediaStream;

  const [activeIdx, setActiveIdx] = useState(0);
  const [scanCount, setScanCount] = useState(0);
  const [scanErr, setScanErr]     = useState(null);
  const [summary, setSummary]     = useState({ faces: 0, vehicles: 0, plates: [] });
  const [flash, setFlash]         = useState(null); // { msg, color, ts } — fades after 1.2s
  const flashTimerRef             = useRef(null);

  const videoRef    = useRef(null);
  const imgRef      = useRef(null);
  const overlayRef  = useRef(null);
  const containerRef = useRef(null);
  const scanningRef = useRef(false);
  const scanTimerRef = useRef(null);
  const startedRef   = useRef(false);
  // Hasil terakhir + pelacak, untuk menggambar kotak mulus di antara dua balasan server.
  const lastResultRef  = useRef(null);   // { scanResult, w, h, vTracks, fTracks, t }
  const vTrackerRef    = useRef(createTracker());
  const fTrackerRef    = useRef(createTracker());
  const rafRef         = useRef(0);
  const imgCycleRef = useRef(null);
  const hlsRef      = useRef(null);
  const webrtcCleanupRef = useRef(null); // cleanup fn dari playWhep (WebRTC/RTSP-gateway)
  const aliveRef    = useRef(true);
  // Per-cell rolling face embedding buffer — averages N consecutive detections
  // of the same face area to get a more stable identity vector for matching.
  const voteBufferRef = useRef(createFaceVoteBuffer({ maxFrames: 5 }));
  // Cell controls state — paused/mute/menu/clock + per-cell scan override
  const [paused, setPaused]       = useState(false);
  const [muted,  setMuted]        = useState(true);
  const [menuOpen, setMenuOpen]   = useState(false);
  const [clock, setClock]         = useState(() => new Date());
  // Per-cell scan toggle. Default ON — global "AUTO-SCAN ON" di header otomatis
  // mengaktifkan semua cell. User bisa opt-out per cell via tombol Eye di bottom
  // bar (selective monitoring). Sebelumnya default OFF → bikin global toggle
  // tidak ada efek terlihat (UX trap), sehingga user kira detector tidak jalan.
  const [cellScanLocal, setCellScanLocal] = useState(true);
  const cellScanOn = cellScanProp ?? cellScanLocal;
  const effectiveScanEnabled = scanEnabled && cellScanOn && scanTurn;
  const scanArmed = scanEnabled && cellScanOn;
  const menuRef = useRef(null);

  // Clock tick (per cell — cheap, supaya sync ke jam tampilan video)
  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Click outside menu to close
  useEffect(() => {
    if (!menuOpen) return;
    const h = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  const togglePause = (e) => { e?.stopPropagation(); setPaused(p => !p); };
  const toggleMute  = (e) => { e?.stopPropagation(); setMuted(m => !m); };

  // Native browser fullscreen — toggle on the cell containerRef.
  // Pakai self-contained ref instead dari parent finding element via querySelector
  // (sebelumnya gagal karena data-attr tidak match).
  const toggleNativeFullscreen = useCallback((e) => {
    e?.stopPropagation();
    const el = containerRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        document.exitFullscreen?.();
      } else {
        (el.requestFullscreen?.()
          || el.webkitRequestFullscreen?.()
          || el.mozRequestFullScreen?.()
          || el.msRequestFullscreen?.()
        )?.catch?.(() => {});
      }
    } catch (err) {
      console.warn('[fullscreen] fail:', err?.message);
    }
  }, []);

  // Track fullscreen state untuk update icon
  const [isNativeFs, setIsNativeFs] = useState(false);
  useEffect(() => {
    const onChange = () => {
      setIsNativeFs(document.fullscreenElement === containerRef.current);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  const fmtClock = (d) => d.toTimeString().slice(0, 8);
  const cellNumber = String(cameraNumber || '').padStart(2, '0');
  // (removed fsActive — theater mode pure in-app via parent state)
  // Alert dedup is now centralized in src/lib/aiScanner.js

  const activeItem = playlist[activeIdx] || null;
  const showImage  = !isLive && isImageItem(activeItem);
  const activeSrc  = isLive ? kamera.stream_url : (activeItem?.url || null);

  // Apply paused/muted to video element (after activeSrc declared)
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (paused) v.pause(); else v.play().catch(() => {});
  }, [paused, activeSrc]);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = muted;
  }, [muted]);

  const takeSnapshot = useCallback(async (e) => {
    e?.stopPropagation();
    setMenuOpen(false);
    let sourceEl = null, w = 0, h = 0;
    if (showImage && imgRef.current) {
      sourceEl = imgRef.current; w = imgRef.current.naturalWidth; h = imgRef.current.naturalHeight;
    } else if (videoRef.current && videoRef.current.readyState >= 2) {
      sourceEl = videoRef.current; w = videoRef.current.videoWidth; h = videoRef.current.videoHeight;
    }
    if (!sourceEl || !w || !h) return;
    try {
      const cap = document.createElement('canvas');
      cap.width = w; cap.height = h;
      cap.getContext('2d').drawImage(sourceEl, 0, 0, w, h);
      cap.toBlob((blob) => {
        if (!blob) return;
        if (typeof onSnapshot === 'function') {
          onSnapshot(blob, kamera);
        } else {
          // Default: download as file
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${kamera.nama || 'snapshot'}-${Date.now()}.jpg`;
          a.click();
          URL.revokeObjectURL(url);
        }
      }, 'image/jpeg', 0.92);
    } catch (err) { console.warn('[snapshot] fail:', err?.message); }
  }, [kamera, onSnapshot, showImage]);

  // Stream error state — surface ke UI supaya user tahu kenapa hitam
  // null | 'rtsp' | 'cors' | 'manifest' | 'network' | 'media' | 'play'
  //      | 'webrtc' | 'gateway' | 'timeout'
  const [streamErr, setStreamErr] = useState(null);

  // Jalur B — MediaStream langsung (getDisplayMedia tangkap tab/layar YouTube).
  // Stream ini LOKAL (bukan lintas-origin) sehingga frame-nya bisa di-drawImage
  // ke canvas tanpa "tainted" → AI scan (wajah/kendaraan/plat + mesh) tetap jalan.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !mediaStream) return;
    setStreamErr(null);
    v.srcObject = mediaStream;
    v.play?.().catch(() => {});
    return () => { try { v.srcObject = null; } catch { /* ignore */ } };
  }, [mediaStream]);

  // Multi-protocol stream setup: HLS (.m3u8) | WebRTC/WHEP | RTSP (via gateway)
  // | file video. Browser tidak bisa play RTSP langsung — kalau gateway
  // (VITE_STREAM_GATEWAY, go2rtc/MediaMTX) dikonfigurasi, RTSP otomatis
  // dirutekan ke WebRTC; kalau tidak, tampilkan panduan yang jelas.
  useEffect(() => {
    if (showImage || mediaStream) return;   // mediaStream ditangani effect di atas
    const v = videoRef.current;
    if (!v || !activeSrc) return;

    setStreamErr(null);
    let disposed = false;
    // State auto-reconnect WebRTC (WHEP tidak punya recovery bawaan seperti hls.js)
    let rtcAttempts = 0;
    let reconnectTimer = null;

    // Bersihkan resource sesi sebelumnya (penting saat src berganti)
    if (hlsRef.current) { try { hlsRef.current.destroy(); } catch { /* ignore */ } hlsRef.current = null; }
    if (webrtcCleanupRef.current) { try { webrtcCleanupRef.current(); } catch { /* ignore */ } webrtcCleanupRef.current = null; }

    // Listener untuk error <video> langsung (file / native HLS): CORS, decode, dll
    const onVideoError = () => {
      const err = v.error;
      const code = err?.code;
      // 1=ABORTED, 2=NETWORK, 3=DECODE, 4=SRC_NOT_SUPPORTED
      const map = { 2: 'network', 3: 'media', 4: 'cors' };
      setStreamErr(map[code] || 'network');
      console.warn('[stream]', kamera?.nama, 'video err:', code, err?.message);
    };
    v.addEventListener('error', onVideoError);

    // ── HLS via hls.js (dipakai .m3u8 langsung & fallback gateway) ─────────────
    const startHls = (url) => {
      if (Hls.isSupported()) {
        // Tuned config untuk CCTV live streams:
        // - lowLatencyMode: lebih responsif
        // - manifestLoadPolicy: retry 3x (naik dari default 1x) — toleran blip
        const hls = new Hls({
          debug: false,
          enableWorker: true,
          lowLatencyMode: true,
          backBufferLength: 30,
          manifestLoadPolicy: {
            default: { maxTimeToFirstByteMs: 10000, maxLoadTimeMs: 20000, timeoutRetry: { maxNumRetry: 3, retryDelayMs: 500, maxRetryDelayMs: 3000 }, errorRetry: { maxNumRetry: 3, retryDelayMs: 500, maxRetryDelayMs: 3000 } },
          },
        });
        hls.loadSource(url);
        hls.attachMedia(v);

        let recoveryAttempts = 0;
        const MAX_RECOVERY = 2;

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setStreamErr(null);
          recoveryAttempts = 0;
          v.play().catch((e) => {
            console.warn('[stream]', kamera?.nama, 'play() reject:', e?.message);
            if (e?.name !== 'NotAllowedError') setStreamErr('play');
          });
        });

        hls.on(Hls.Events.ERROR, (_evt, data) => {
          if (!data?.fatal) return;
          const type = data.type;
          console.warn('[stream]', kamera?.nama, 'HLS fatal:', type, data?.details);

          // Auto-recovery untuk transient errors — hls.js best practice
          if (recoveryAttempts < MAX_RECOVERY) {
            recoveryAttempts++;
            if (type === Hls.ErrorTypes.NETWORK_ERROR) {
              console.log('[stream] auto-recover network -> startLoad()');
              try { hls.startLoad(); return; } catch { /* fall through */ }
            }
            if (type === Hls.ErrorTypes.MEDIA_ERROR) {
              console.log('[stream] auto-recover media -> recoverMediaError()');
              try { hls.recoverMediaError(); return; } catch { /* fall through */ }
            }
          }

          const map = { networkError: 'cors', mediaError: 'media' };
          const key = type?.replace('Error', '') + 'Error';
          setStreamErr(map[key] || 'manifest');
          try { hls.destroy(); } catch { /* ignore */ }
        });

        hlsRef.current = hls;
      } else if (v.canPlayType('application/vnd.apple.mpegurl')) {
        v.src = url; // Safari / iOS punya HLS native
      } else {
        setStreamErr('manifest');
      }
    };

    // ── WebRTC / WHEP (dipakai sumber webrtc & RTSP via gateway) ───────────────
    // Error transient (jaringan/ICE putus, gateway sesaat) → auto-reconnect
    // dengan backoff. Error konfigurasi permanen (cors) → langsung tampilkan,
    // percuma retry. hls.js sudah punya recovery sendiri; WebRTC belum, dan
    // tanpa ini satu blip jaringan bikin cell hitam permanen sampai remount.
    const MAX_RTC_RETRY = 5;
    const RETRYABLE_RTC = new Set(['timeout', 'webrtc', 'gateway', 'network']);
    const startWebRtc = (whepUrl, registerRtsp = null) => {
      if (disposed) return;
      webrtcCleanupRef.current = playWhep(v, whepUrl, {
        registerRtsp,
        onConnected: () => { if (!disposed) { rtcAttempts = 0; setStreamErr(null); } },
        onError: (code) => {
          if (disposed || !code) return;
          if (RETRYABLE_RTC.has(code) && rtcAttempts < MAX_RTC_RETRY) {
            rtcAttempts++;
            const delay = Math.min(1000 * 2 ** rtcAttempts, 15000); // 2s,4s,8s,15s,15s
            console.warn(`[stream] ${kamera?.nama} WebRTC ${code} — reconnect #${rtcAttempts} dalam ${delay}ms`);
            setStreamErr(null); // sembunyikan overlay error selama masih retry
            reconnectTimer = setTimeout(() => {
              reconnectTimer = null;
              startWebRtc(whepUrl, registerRtsp);
            }, delay);
          } else {
            setStreamErr(code); // permanen atau retry habis
          }
        },
      });
    };

    const type = detectStreamType(activeSrc);
    // Halaman HTTPS tidak bisa memuat sumber HTTP (mixed content). Untuk RTSP
    // yang dirutekan via gateway HTTPS, blokir ini tidak relevan — jadi cek
    // hanya untuk sumber langsung (webrtc/hls/file).
    if (type !== 'rtsp' && isMixedContentBlocked(activeSrc)) {
      setStreamErr('mixed');
    } else if (type === 'rtsp') {
      // RTSP tidak bisa native. Rutekan via gateway kalau ada, jika tidak panduan.
      const whep = gatewayWhepUrlForRtsp(activeSrc);
      if (whep) startWebRtc(whep, activeSrc);
      else setStreamErr('rtsp');
    } else if (type === 'webrtc') {
      startWebRtc(activeSrc);
    } else if (type === 'hls') {
      startHls(activeSrc);
    } else {
      // file video / unknown → coba langsung di <video src>
      v.src = activeSrc;
    }

    return () => {
      disposed = true;
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
      v.removeEventListener('error', onVideoError);
      if (hlsRef.current) { try { hlsRef.current.destroy(); } catch { /* ignore */ } hlsRef.current = null; }
      if (webrtcCleanupRef.current) { try { webrtcCleanupRef.current(); } catch { /* ignore */ } webrtcCleanupRef.current = null; }
    };
  }, [activeSrc, showImage, kamera?.nama]);

  const handleVideoEnded = () => {
    if (!isLive && playlist.length > 1) {
      setActiveIdx(i => (i + 1) % playlist.length);
    }
  };
  useEffect(() => {
    if (!showImage || playlist.length <= 1) return;
    imgCycleRef.current = setInterval(() => {
      setActiveIdx(i => (i + 1) % playlist.length);
    }, 5000);
    return () => clearInterval(imgCycleRef.current);
  }, [showImage, playlist.length]);

  // Reset overlay when source changes
  useEffect(() => {
    setSummary({ faces: 0, vehicles: 0, plates: [] });
    const cvs = overlayRef.current;
    if (cvs) cvs.getContext('2d').clearRect(0, 0, cvs.width, cvs.height);
  }, [activeSrc, activeIdx]);

  // ── Scan: capture → callHFAnalyze → enrichScanResult → renderAllBboxes → dispatchAlerts ──
  const runScan = useCallback(async () => {
    if (scanningRef.current || !effectiveScanEnabled || !aliveRef.current) return;

    let sourceEl = null, w = 0, h = 0;
    if (showImage && imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      sourceEl = imgRef.current;
      w = imgRef.current.naturalWidth;
      h = imgRef.current.naturalHeight;
    } else if (videoRef.current && videoRef.current.readyState >= 2) {
      sourceEl = videoRef.current;
      w = videoRef.current.videoWidth;
      h = videoRef.current.videoHeight;
    }
    if (!sourceEl || !w || !h) return;

    scanningRef.current = true;
    try {
      // Kirim frame terbaru dalam ukuran kecil; bbox balasan diskalakan balik ke ruang sumber (k).
      const k = w > SEND_MAX_W ? w / SEND_MAX_W : 1;
      const blob = await captureFrame(sourceEl, Math.round(w / k), Math.round(h / k));
      if (!blob) { setScanErr('CORS / capture'); return; }

      // Kendaraan yang platnya sudah terbaca tidak di-OCR ulang (OCR CPU adalah tahap paling lambat di server).
      const known = vTrackerRef.current.snapshot().filter(t => t.data.plateEntry).map(t => t.bbox.map(v => Math.round(v / k)));
      const rawData = scaleRawBoxes(
        await callHFAnalyze(blob, 60000, known.length ? { known_vehicles: JSON.stringify(known) } : null), k);
      if (!aliveRef.current) return;

      // Multi-frame vote: average embedding dari N frame berturutan untuk wajah
      // yang sama (di-track via IoU bbox overlap). Mengurangi noise per-frame
      // dan meningkatkan akurasi pencocokkan saat wajah sedikit bergerak/miring.
      if (rawData.faces?.length && voteBufferRef.current) {
        const nowMs = Date.now();
        for (const f of rawData.faces) {
          if (f.bbox && Array.isArray(f.embedding) && f.embedding.length) {
            const vote = voteBufferRef.current.update(f.bbox, f.embedding, nowMs);
            f.embedding = vote.embedding;   // ganti dengan rata-rata N frame
            f._voteFrames = vote.frames;    // dipakai enrichScanResult untuk adaptive threshold
          }
        }
      }

      // F-001: render YELLOW "Memindai..." bbox SEGERA setelah YOLO return,
      // sebelum DB lookup. User lihat AI sedang identifikasi (bukan layar kosong).
      renderRawBboxes(overlayRef.current, w, h, rawData);
      // Pass sourceEl untuk Tier-2 vision OCR plate (F0-A7)
      const scanResult = await enrichScanResult(rawData, embeddingsRef, sourceEl);
      if (!aliveRef.current) return;

      // Lacak kendaraan/wajah antar-hasil: id stabil -> plat yang sudah terbaca dipakai ulang, kotak bisa diekstrapolasi.
      const nowT = Date.now();
      const vTracks = vTrackerRef.current.update(scanResult.vehicleObjs.map(o => o.bbox), nowT);
      const fTracks = fTrackerRef.current.update(scanResult.enrichedFaces.map(f => f.bbox), nowT);
      scanResult.vehicleObjs.forEach((o, i) => {
        const tr = vTracks[i];
        const hit = scanResult.enrichedPlates.find(p => p.plate && p.from_bbox && inside(p.from_bbox, o.bbox));
        if (hit) tr.data.plateEntry = hit;
        else if (tr.data.plateEntry) scanResult.enrichedPlates.push({ ...tr.data.plateEntry, from_bbox: o.bbox });
      });
      lastResultRef.current = { scanResult, w, h, vTracks, fTracks, t: nowT };
      // Replace YELLOW dengan GREEN/RED final state setelah enrichment selesai.
      renderAllBboxes(overlayRef.current, w, h, scanResult);
      const alertCount = await dispatchAlerts(scanResult, { id: kamera.id, nama: kamera.nama, lokasi: kamera.lokasi });
      // Log semua deteksi ke Supabase log_aktivitas (dedup window per subjek)
      logScanResult(scanResult, { id: kamera.id, nama: kamera.nama, lokasi: kamera.lokasi });

      setSummary({
        faces: scanResult.enrichedFaces.length,
        vehicles: scanResult.vehicleObjs.length,
        plates: scanResult.enrichedPlates,
      });
      setScanCount(c => c + 1);
      setScanErr(null);

      // Surface scan result to parent — used by Upload File / Batch Scan to fill right panel
      if (typeof onScanResult === 'function') {
        try { onScanResult(scanResult); } catch (e) { console.warn('[onScanResult] handler error:', e?.message); }
      }

      // Flash a per-scan notification with the detection count
      const fc = scanResult.enrichedFaces.length;
      const vc = scanResult.vehicleObjs.length;
      const pc = scanResult.enrichedPlates.length;
      const hasThreat = scanResult.enrichedFaces.some(f => f._person?.status_intelijen === 'WANTED' || f._person?.status_intelijen === 'SUSPECT')
                     || scanResult.enrichedPlates.some(p => p.info?.intel === 'WANTED' || p.info?.intel === 'SUSPECT');
      if (fc + vc + pc > 0) {
        const parts = [];
        if (fc) parts.push(`${fc} wajah`);
        if (vc) parts.push(`${vc} kendaraan`);
        if (pc) parts.push(`${pc} plat`);
        const msg = (alertCount > 0 ? `⚠ ALERT · ` : '✓ ') + parts.join(' · ');
        clearTimeout(flashTimerRef.current);
        setFlash({ msg, color: hasThreat ? '#ff2d55' : '#00ff88', ts: Date.now() });
        flashTimerRef.current = setTimeout(() => setFlash(null), 1500);
      }
    } catch (e) {
      if (e?.name === 'AbortError') setScanErr('AI timeout');
      else setScanErr(e?.message || 'scan error');
    } finally {
      scanningRef.current = false;
    }
  }, [showImage, effectiveScanEnabled, embeddingsRef, kamera.id, kamera.nama, kamera.lokasi]);

  // Gambar ulang kotak ~OVERLAY_FPS kali/detik dari hasil terakhir + ekstrapolasi gerak, di antara dua balasan server.
  useEffect(() => {
    let last = 0;
    const draw = (ts) => {
      rafRef.current = requestAnimationFrame(draw);
      if (ts - last < 1000 / OVERLAY_FPS) return;
      last = ts;
      const r = lastResultRef.current;
      const cv = overlayRef.current;
      if (!r || !cv) return;
      const now = Date.now();
      if (now - r.t > RESULT_MAX_AGE_MS) {            // balasan berhenti datang: jangan biarkan kotak basi menempel
        cv.getContext('2d').clearRect(0, 0, cv.width, cv.height);
        lastResultRef.current = null;
        return;
      }
      const { scanResult, vTracks, fTracks } = r;
      const vehicleObjs = scanResult.vehicleObjs.map((o, i) => ({ ...o, bbox: extrapolate(vTracks[i], now) }));
      const enrichedFaces = scanResult.enrichedFaces.map((f, i) => ({ ...f, bbox: extrapolate(fTracks[i], now) }));
      const enrichedPlates = scanResult.enrichedPlates.map((p) => {
        const vi = p.from_bbox ? scanResult.vehicleObjs.findIndex(o => inside(p.from_bbox, o.bbox)) : -1;
        if (vi < 0) return p;
        const a = scanResult.vehicleObjs[vi].bbox, b = vehicleObjs[vi].bbox;
        const dx = b[0] - a[0], dy = b[1] - a[1];
        const fb = p.from_bbox;
        return { ...p, from_bbox: [fb[0] + dx, fb[1] + dy, fb[2] + dx, fb[3] + dy] };
      });
      renderAllBboxes(cv, r.w, r.h, { ...scanResult, vehicleObjs, enrichedFaces, enrichedPlates });
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // Scan loop with stagger
  useEffect(() => {
    aliveRef.current = true;
    if (!effectiveScanEnabled) {
      clearTimeout(scanTimerRef.current);
      startedRef.current = false;
      return;
    }
    if (startedRef.current) return; // already running
    startedRef.current = true;
    // Tanpa antre: permintaan berikut baru dimulai SETELAH balasan kembali, dan frame yang dikirim selalu yang
    // terbaru. Jeda minimum = min(scanInterval, MIN_GAP_MS), dihitung dari awal permintaan.
    const gap = Math.min(scanInterval, MIN_GAP_MS);
    let stopped = false;
    const loop = async () => {
      if (stopped || !aliveRef.current) return;
      const t1 = performance.now();
      await runScan();
      if (stopped) return;
      scanTimerRef.current = setTimeout(loop, Math.max(0, gap - (performance.now() - t1)));
    };
    const t0 = setTimeout(loop, scanDelayMs + 800); // stagger + initial source-load delay
    return () => {
      stopped = true;
      clearTimeout(t0);
      clearTimeout(scanTimerRef.current);
      startedRef.current = false;
      aliveRef.current = false;
    };
  }, [runScan, scanInterval, scanDelayMs, effectiveScanEnabled]);

  useEffect(() => () => {
    aliveRef.current = false;
    clearTimeout(scanTimerRef.current);
    cancelAnimationFrame(rafRef.current);
    clearInterval(imgCycleRef.current);
    clearTimeout(flashTimerRef.current);
    if (hlsRef.current) { try { hlsRef.current.destroy(); } catch { /* ignore */ } }
  }, []);

  // Theater mode (YouTube-style): in-app layout shift via parent state.
  // BUKAN browser fullscreen — kamera pilihan jadi besar di area app, kamera
  // lain mengecil jadi thumbnail strip. Dilakukan di parent (NeoVision FeedTab).
  const toggleMaximize = useCallback(() => {
    if (typeof onToggleMaximize === 'function') onToggleMaximize();
  }, [onToggleMaximize]);

  // ── Render ───────────────────────────────────────────────────────────────────
  const showMaxStyle = isMaximized;
  // Theater (in-app): kamera maximized membesar di area parent (NeoVision
  // mengatur tinggi via flex), border + glow cyan. TIDAK browser fullscreen.
  return (
    <div ref={containerRef}
      className={`relative overflow-hidden group rounded-2xl${isMaximized ? ' h-full' : ''}`}
      style={{
        background: '#000',
        border: showMaxStyle ? '2px solid var(--color-cyan)' : '1px solid var(--border-dim)',
        aspectRatio: isMaximized ? undefined : '16/9',
        width: '100%',
        boxShadow: showMaxStyle ? '0 0 32px rgba(0,212,255,0.35)' : 'none',
      }}>

      {showImage ? (
        <img ref={imgRef}
          src={activeSrc}
          alt={kamera.nama}
          crossOrigin="anonymous"
          className="w-full h-full object-cover"
          onError={() => setScanErr('image load failed')} />
      ) : (activeSrc || mediaStream) ? (
        <video ref={videoRef}
          autoPlay loop={!isLive && playlist.length <= 1}
          muted playsInline crossOrigin="anonymous"
          onEnded={handleVideoEnded}
          className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-xs"
          style={{ color: 'var(--text-mute)' }}>
          Tidak ada feed
        </div>
      )}

      {/* Stream error overlay — surface kenapa cell hitam:
          rtsp: protokol tidak bisa diputar di browser (butuh gateway)
          webrtc/gateway/timeout: negosiasi WebRTC / gateway gagal
          cors: server stream tidak izinkan akses cross-origin
          manifest: m3u8 file tidak bisa diparse / 404
          media: codec tidak didukung browser
          network: koneksi gagal */}
      {streamErr && !showImage && activeSrc && (
        <div className="absolute inset-0 flex items-center justify-center p-4"
          style={{ background: 'rgba(2,6,14,0.7)' }}>
          <div className="rounded-2xl px-4 py-3 max-w-[90%]"
            style={{ background: 'rgba(255,45,85,0.12)', border: '1px solid rgba(255,45,85,0.4)' }}>
            <div className="flex items-center gap-2 mb-1">
              <span style={{ fontSize: 14 }}>⚠</span>
              <span className="text-[10px] font-black font-mono uppercase tracking-widest" style={{ color: '#ff6b7a' }}>
                STREAM ERROR
              </span>
            </div>
            <p className="text-[10px] font-mono" style={{ color: 'var(--text-hi)' }}>
              {streamErr === 'mixed'
                ? 'Halaman HTTPS tidak bisa memuat stream HTTP (mis. 192.168.x.x di LAN). Ekspos kamera/gateway lewat HTTPS (Cloudflare Tunnel atau reverse proxy TLS), lalu pakai URL https://.'
                : streamErr === 'rtsp'
                ? (hasGateway()
                    ? 'RTSP via gateway gagal. Pastikan gateway (go2rtc / MediaMTX) online dan kamera RTSP bisa diakses dari gateway.'
                    : 'RTSP tidak bisa diputar langsung di browser. Atur gateway streaming (go2rtc / MediaMTX) lalu isi VITE_STREAM_GATEWAY, atau pakai URL HLS (.m3u8) / WebRTC (WHEP).')
                : streamErr === 'webrtc'
                ? 'Koneksi WebRTC gagal terbentuk. Cek firewall / STUN, atau coba URL HLS (.m3u8) sebagai alternatif.'
                : streamErr === 'gateway'
                ? 'Gateway menolak permintaan stream (cek nama sumber / status kamera di gateway).'
                : streamErr === 'timeout'
                ? 'Stream timeout: tidak ada respons dari server dalam batas waktu. Cek koneksi & status CCTV.'
                : streamErr === 'cors'
                ? 'Server stream blokir cross-origin (CORS). Minta admin CCTV allow domain ini, atau pakai gateway WebRTC.'
                : streamErr === 'manifest'
                ? 'Manifest m3u8 tidak bisa diparse (404 atau format salah). Cek URL di tab browser langsung.'
                : streamErr === 'media'
                ? 'Codec video tidak didukung. Pakai H.264 (AVC) untuk kompatibilitas browser maksimum.'
                : streamErr === 'play'
                ? 'Browser blokir autoplay. Klik cell untuk mulai play manual.'
                : 'Stream tidak bisa dimuat. Cek URL & status CCTV.'}
            </p>
          </div>
        </div>
      )}

      {/* Bbox overlay */}
      <canvas ref={overlayRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ objectFit: 'cover' }} />

      {/* Top header bar — number + name + actions (eye, snapshot, more)
          opacity 0 by default → 100 on hover (cell jadi rapi tanpa overlay tetap) */}
      {!isTheatreMini && (
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center gap-2 px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
          style={{ background: 'linear-gradient(to bottom, rgba(2,6,14,0.85), transparent)' }}>
          <div className="w-1.5 h-1.5 rounded-full animate-blink shrink-0"
            style={{ background: isLive ? '#ff2d55' : '#00ff88' }} />
          <span className="text-[10px] font-black font-mono shrink-0" style={{ color: '#fff', opacity: 0.9 }}>
            {cellNumber}
          </span>
          <span className="text-[11px] font-bold truncate" style={{ color: '#fff' }}>
            {kamera.nama}
          </span>
          <div className="flex-1" />
          {/* Action icons */}
          <button onClick={(e) => { e.stopPropagation(); onToggleMaximize?.(); }}
            className="flex items-center justify-center rounded transition-all hover:opacity-80"
            style={{ width: 22, height: 22, background: 'rgba(255,255,255,0.06)' }}
            title="Preview">
            <Eye size={11} color="#fff" opacity={0.85} />
          </button>
          <button onClick={takeSnapshot}
            className="flex items-center justify-center rounded transition-all hover:opacity-80"
            style={{ width: 22, height: 22, background: 'rgba(255,255,255,0.06)' }}
            title="Snapshot">
            <Camera size={11} color="#fff" opacity={0.85} />
          </button>
          <div className="relative" ref={menuRef}>
            <button onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o); }}
              className="flex items-center justify-center rounded transition-all hover:opacity-80"
              style={{ width: 22, height: 22, background: menuOpen ? 'rgba(0,212,255,0.20)' : 'rgba(255,255,255,0.06)' }}
              title="Menu">
              <MoreVertical size={11} color="#fff" opacity={0.85} />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-1 rounded-lg overflow-hidden z-20"
                style={{ background: 'var(--color-card)', border: '1px solid var(--border-dim)', boxShadow: '0 8px 24px rgba(0,0,0,0.45)', minWidth: 140 }}>
                {onEdit && (
                  <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onEdit(kamera); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-[10px] font-mono hover:bg-white/5"
                    style={{ color: 'var(--text-hi)' }}>
                    <Pencil size={11} /> Edit
                  </button>
                )}
                <button onClick={takeSnapshot}
                  className="w-full flex items-center gap-2 px-3 py-2 text-[10px] font-mono hover:bg-white/5"
                  style={{ color: 'var(--text-hi)' }}>
                  <Camera size={11} /> Snapshot
                </button>
                {onDelete && (
                  <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); if (confirm(`Hapus kamera "${kamera.nama}"?`)) onDelete(kamera); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-[10px] font-mono hover:bg-white/5"
                    style={{ color: '#ff2d55', borderTop: '1px solid var(--border-lo)' }}>
                    <Trash2 size={11} /> Hapus
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* LIVE badge — top right (separate from header) */}
      {activeSrc && !isTheatreMini && (
        <div className="absolute top-11 right-3 flex items-center gap-1.5 px-2 py-0.5 rounded z-10"
          style={{ background: 'rgba(2,6,14,0.85)', backdropFilter: 'blur(4px)' }}>
          <div className="w-1.5 h-1.5 rounded-full animate-blink"
            style={{ background: isLive ? '#ff2d55' : '#00ff88' }} />
          <span className="text-[8px] font-black font-mono"
            style={{ color: isLive ? '#ff2d55' : '#00ff88' }}>
            {isLive ? 'LIVE' : showImage ? 'IMG' : 'REC'}
          </span>
        </div>
      )}

      {/* Theatre mini — compact label only */}
      {isTheatreMini && (
        <div className="absolute top-1 left-1 right-1 flex items-center gap-1 px-1.5 py-0.5 rounded"
          style={{ background: 'rgba(2,6,14,0.85)' }}>
          <div className="w-1 h-1 rounded-full animate-blink shrink-0" style={{ background: isLive ? '#ff2d55' : '#00ff88' }} />
          <span className="text-[7px] font-black font-mono truncate" style={{ color: '#fff' }}>
            {cellNumber} {kamera.nama}
          </span>
        </div>
      )}

      {/* Per-scan flash notification — fades after 1.5s */}
      {flash && (
        <div key={flash.ts}
          className="absolute top-9 left-2 flex items-center gap-1.5 px-2.5 py-1 rounded-lg"
          style={{
            background: flash.color === '#ff2d55' ? 'rgba(255,45,85,0.95)' : 'rgba(0,255,136,0.95)',
            color: flash.color === '#ff2d55' ? '#fff' : '#02060e',
            boxShadow: `0 0 16px ${flash.color}66`,
            border: `1px solid ${flash.color}`,
            animation: 'asp-flash 1.4s ease-out forwards',
          }}>
          <span className="text-[9px] font-black font-mono">{flash.msg}</span>
        </div>
      )}
      <style>{`
        @keyframes asp-flash {
          0%   { opacity: 0; transform: translateY(-4px) scale(0.96); }
          15%  { opacity: 1; transform: translateY(0) scale(1.02); }
          30%  { transform: scale(1); }
          80%  { opacity: 1; }
          100% { opacity: 0; transform: translateY(0) scale(1); }
        }
      `}</style>

      {/* Scan count overlay — small, hidden in theatre-mini */}
      {effectiveScanEnabled && !isTheatreMini && (
        <div className="absolute top-11 right-20 flex items-center gap-1 px-2 py-0.5 rounded text-[8px] font-mono z-10"
          style={{ background: 'rgba(2,6,14,0.78)', color: scanErr ? '#ff2d55' : '#00ff88' }}
          title={scanErr || `${summary.faces} wajah · ${summary.vehicles} kendaraan`}>
          <Eye size={9} />#{scanCount}
        </div>
      )}

      {/* Bottom controls bar — pause / mute / time | theater / fullscreen
          opacity 0 by default → 100 on hover */}
      {!isTheatreMini && (
        <div className="absolute bottom-0 left-0 right-0 z-10 flex items-center gap-2 px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
          style={{ background: 'linear-gradient(to top, rgba(2,6,14,0.92), transparent)' }}>
          <button onClick={togglePause}
            className="flex items-center justify-center rounded hover:opacity-80"
            style={{ width: 22, height: 22 }}
            title={paused ? 'Play' : 'Pause'}>
            {paused ? <Play size={12} color="#fff" /> : <Pause size={12} color="#fff" />}
          </button>
          {/* Per-cell scan toggle — independen dari global */}
          <button onClick={(e) => { e.stopPropagation(); if (onCellScanToggle) onCellScanToggle(); else setCellScanLocal(v => !v); }}
            className="flex items-center justify-center rounded transition-all hover:opacity-80"
            style={{
              width: 24, height: 24,
              background: scanArmed ? 'rgba(0,255,136,0.45)' : 'rgba(148,163,184,0.30)',
              border: `1.5px solid ${scanArmed ? '#00ff88' : 'rgba(148,163,184,0.6)'}`,
              boxShadow: scanArmed ? '0 0 8px rgba(0,255,136,0.5)' : 'none',
            }}
            title={cellScanOn ? t('cell_scan_off') : t('cell_scan_on')}>
            <Eye size={13} color={scanArmed ? '#fff' : '#cbd5e1'} strokeWidth={scanArmed ? 2.5 : 2} />
          </button>
          <button onClick={toggleMute}
            className="flex items-center justify-center rounded hover:opacity-80"
            style={{ width: 22, height: 22 }}
            title={muted ? 'Unmute' : 'Mute'}>
            {muted ? <VolumeX size={12} color="#fff" opacity={0.7} /> : <Volume2 size={12} color="#fff" />}
          </button>
          <span className="text-[10px] font-mono" style={{ color: 'var(--color-cyan)' }}>
            {fmtClock(clock)}
          </span>
          <div className="flex-1" />
          {/* Theater toggle */}
          <button onClick={(e) => { e.stopPropagation(); onToggleMaximize?.(); }}
            className="flex items-center justify-center rounded hover:opacity-80"
            style={{ width: 22, height: 22, background: isMaximized ? 'rgba(0,212,255,0.25)' : 'transparent', border: isMaximized ? '1px solid rgba(0,212,255,0.5)' : 'none' }}
            title="Theater Mode">
            <Tv size={12} color={isMaximized ? 'var(--color-cyan)' : '#fff'} />
          </button>
          {/* Fullscreen toggle (native browser, self-contained) */}
          <button onClick={toggleNativeFullscreen}
            className="flex items-center justify-center rounded hover:opacity-80"
            style={{ width: 22, height: 22 }}
            title={isNativeFs ? 'Exit Fullscreen (ESC)' : 'Fullscreen'}>
            {isNativeFs
              ? <Minimize2 size={12} color="#fff" />
              : <Maximize2 size={12} color="#fff" />}
          </button>
        </div>
      )}

      {/* Lokasi + summary chips di kiri-tengah saat normal mode */}
      {!isTheatreMini && (kamera.lokasi || summary.faces > 0 || summary.vehicles > 0 || summary.plates.length > 0) && (
        <div className="absolute left-3 z-10" style={{ bottom: 36 }}>
          {kamera.lokasi && (
            <div className="flex items-center gap-1 mb-1">
              <MapPin size={9} color="rgba(255,255,255,0.5)" />
              <p className="text-[9px] font-mono truncate" style={{ color: scanErr ? '#ff2d55' : 'rgba(207,216,227,0.7)', maxWidth: 240 }}>
                {scanErr ? `⚠ ${scanErr}` : kamera.lokasi}
              </p>
            </div>
          )}
        {(summary.faces > 0 || summary.vehicles > 0 || summary.plates.length > 0) && (
          <div className="flex flex-wrap gap-1">
            {summary.faces > 0 && (
              <div className="text-[7px] font-mono px-1.5 py-0.5 rounded font-black"
                style={{ background: 'rgba(0,212,255,0.30)', color: '#00d4ff' }}>
                {summary.faces} wajah
              </div>
            )}
            {summary.vehicles > 0 && (
              <div className="text-[7px] font-mono px-1.5 py-0.5 rounded font-black"
                style={{ background: 'rgba(255,107,43,0.30)', color: '#ffb088' }}>
                {summary.vehicles} kendaraan
              </div>
            )}
            {summary.plates.slice(0, 3).map((p, i) => {
              const wanted = p.info?.intel === 'WANTED' || p.info?.intel === 'SUSPECT';
              return (
                <div key={i} className="text-[7px] font-mono px-1.5 py-0.5 rounded font-black"
                  style={{
                    background: wanted ? 'rgba(255,45,85,0.45)' : 'rgba(255,214,10,0.30)',
                    color: '#fff',
                    border: wanted ? '1px solid rgba(255,45,85,0.7)' : 'none',
                  }}>
                  {p.plate}{p.info?.nama_pemilik ? ` · ${p.info.nama_pemilik}` : (p.info ? '' : ' · ?')}
                </div>
              );
            })}
          </div>
        )}
        </div>
      )}
    </div>
  );
}
